import { Fragment, useRef, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import scrollIntoView from 'scroll-into-view';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

// components
import { SbSharedTitle } from '../SbSharedTitle/SbSharedTitle';
import { SbSharedPageBreadcrumbs } from '../SbSharedPageBreadcrumbs/SbSharedPageBreadcrumbs';
import { SharedFollowSearchProvider } from '../../shared/SharedFollowSearchProvider/SharedFollowSearchProvider';
import { SharedFollowTopButton } from '../../shared/SharedFollowTopButton/SharedFollowTopButton';
import { SharedCreatorsRootHeadingsWrapper } from '../../shared/SharedCreatorsRootHeadingsWrapper/SharedCreatorsRootHeadingsWrapper';
import { SharedWordpressArticles } from '../../shared/SharedWordpressArticles/SharedWordpressArticles';
import { SharedRespLinksBlock } from '../../shared/SharedRespLinksBlock/SharedRespLinksBlock';
import { SbSharedRelatedCreators } from '../SbSharedRelatedCreators/SbSharedRelatedCreators';
import { SbSharedRelatedSellers } from '../SbSharedRelatedSellers/SbSharedRelatedSellers';
import { SbRespFollowSearchBannerLazy } from '../SbRespFollowSearchBanner/SbRespFollowSearchBannerLazy';
import { SharedRelatedPages } from '../../shared/SharedRelatedPages/SharedRelatedPages';
import { SbRespDesignTiles } from '../SbRespDesignTiles/SbRespDesignTiles';
import { SbSharedTopTileController } from '../SbSharedTopTileController/SbSharedTopTileController';
import { SbSharedCategoryTabs } from '../SbSharedCategoryTabs/SbSharedCategoryTabs';
import { SbSharedCreatorTopContent } from '../SbSharedCreatorTopContent/SbSharedCreatorTopContent';
import { SbRespCreatorDesigns } from '../SbSharedCreatorDesigns/SbRespCreatorDesigns';
import { SbSharedRelatedCreatorBuyPages } from '../SbSharedRelatedCreatorBuyPages/SbSharedRelatedCreatorBuyPages';
import { SharedFAQ } from '../../shared/SharedFAQ/SharedFAQ';
import { SharedBottomModule } from '../../shared/SharedBottomModule/SharedBottomModule';
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy';

// constants
import { ICONIC_DESIGN_MODULE_TYPE } from '../../constants/contentModuleConstants';
import { REVEAL_RELATED_CREATORS_COUNT_DESKTOP } from '../../constants/sbConstants';

// styles
import styles from './SbRespCreatorsComponents.scss';

const SbRespRecentSalesWrapperLazy = lazy(() =>
    import(
        /* webpackChunkName: "SbSharedRecentSalesWrapper" */ '../SbSharedRecentSalesWrapper/SbSharedRecentSalesWrapper'
    )
);

const SbRespCreatorsComponentsComponent = props => {
    const { viewer, currency, itemSearch, user, uriRef, isClient } = props;

    const recentSalesScrollRef = useRef(null);
    const relatedCreatorsScrollRef = useRef(null);
    const relatedIconicDesignScrollRef = useRef(null);

    const { isWordpressArticlesEnabled, isRelatedCreatorsModuleEnabled, isRelatedBuyPagesEnabled } =
        viewer;

    const browseContentModule = itemSearch?.browseContentModule;
    const bottomModule = itemSearch?.bottomModule;
    const showContentModule =
        browseContentModule?.moduleType === ICONIC_DESIGN_MODULE_TYPE &&
        browseContentModule?.title &&
        browseContentModule?.mainImage;

    const scrollToRef = RefObj => {
        const gridEl = RefObj?.current;
        if (gridEl) {
            scrollIntoView(
                gridEl,
                {
                    time: 500,
                },
                () => gridEl.focus()
            );
        }
    };

    return (
        <SharedFollowSearchProvider user={user} itemSearch={itemSearch} uriRef={uriRef}>
            {({
                showFollowButton,
                isFollowing,
                followSearchValue,
                refetchFollow,
                isCategorySearch,
            }) => (
                <Fragment>
                    <SbSharedPageBreadcrumbs itemSearch={itemSearch} user={user} />
                    <SharedCreatorsRootHeadingsWrapper
                        itemSearch={itemSearch}
                        showContentModule={showContentModule}
                    >
                        {Heading => (
                            <>
                                {/* Page Header */}
                                <div className={styles.headerContainer}>
                                    <SbSharedTitle
                                        itemSearch={itemSearch}
                                        user={user}
                                        Heading={Heading}
                                    />
                                    <SharedFollowTopButton
                                        user={user}
                                        showFollowButton={showFollowButton}
                                        followSearchValue={followSearchValue}
                                        isFollowing={isFollowing}
                                        refetchFollow={refetchFollow}
                                        isCategorySearch={isCategorySearch}
                                        itemSearch={itemSearch}
                                        className={styles.followTopButton}
                                    />
                                </div>
                                {/* Content Module */}
                                <SbSharedCreatorTopContent
                                    itemSearch={itemSearch}
                                    user={user}
                                    creatorRootPage={viewer?.creatorRootPage}
                                    onClickViewMoreCreators={() =>
                                        scrollToRef(relatedCreatorsScrollRef)
                                    }
                                    onClickViewMoreSales={() => scrollToRef(recentSalesScrollRef)}
                                    currency={currency}
                                />

                                {/* Broad Search Category Tiles, or, for trade users, Price Range Tiles or On-Approval banner)*/}
                                <SbSharedTopTileController
                                    itemSearch={itemSearch}
                                    designTiles={
                                        <SbRespDesignTiles
                                            itemSearch={itemSearch}
                                            designPageInfo={itemSearch?.designPageInfo}
                                            onViewMoreClick={() =>
                                                scrollToRef(relatedIconicDesignScrollRef)
                                            }
                                        />
                                    }
                                    viewer={viewer}
                                />

                                {/* Results based on verticals */}
                                <SbSharedCategoryTabs
                                    viewer={viewer}
                                    creatorRootPage={viewer?.creatorRootPage}
                                    itemSearch={itemSearch}
                                />

                                {/* Related buy pages for the creator */}
                                {isRelatedBuyPagesEnabled && (
                                    <SbSharedRelatedCreatorBuyPages
                                        creatorRootPage={viewer?.creatorRootPage}
                                    />
                                )}

                                {/* Related transaction data */}
                                {isClient && (
                                    <div ref={recentSalesScrollRef}>
                                        <Suspense fallback="">
                                            <SbRespRecentSalesWrapperLazy
                                                uriRef={uriRef}
                                                currency={currency}
                                            />
                                        </Suspense>
                                    </div>
                                )}

                                {/* Related Sellers for creator pages */}
                                <SbSharedRelatedSellers
                                    creatorRootPage={viewer?.creatorRootPage}
                                    itemSearch={itemSearch}
                                    user={user}
                                    revealCreatorCount={REVEAL_RELATED_CREATORS_COUNT_DESKTOP}
                                />

                                {/* Related Creators for creator pages */}
                                {isRelatedCreatorsModuleEnabled && (
                                    <div ref={relatedCreatorsScrollRef}>
                                        <SbSharedRelatedCreators
                                            itemSearch={itemSearch}
                                            revealCreatorCount={
                                                REVEAL_RELATED_CREATORS_COUNT_DESKTOP
                                            }
                                        />
                                    </div>
                                )}

                                {/*  Creator Designs */}
                                <div ref={relatedIconicDesignScrollRef}>
                                    <SbRespCreatorDesigns
                                        designPageInfo={itemSearch?.designPageInfo}
                                        currency={currency}
                                    />
                                </div>

                                {/* Related Wordpress Articles */}
                                {isWordpressArticlesEnabled && (
                                    <SharedWordpressArticles itemSearch={itemSearch} />
                                )}

                                {/* Follow Search */}
                                {isClient && (
                                    <Suspense fallback="">
                                        <SbRespFollowSearchBannerLazy
                                            user={user}
                                            showFollowButton={showFollowButton}
                                            isFollowing={isFollowing}
                                            followSearchValue={followSearchValue}
                                            uriRef={uriRef}
                                            buttonSize="large"
                                            refetchFollow={refetchFollow}
                                            isCategorySearch={isCategorySearch}
                                            itemSearch={itemSearch}
                                        />
                                    </Suspense>
                                )}

                                {/* Bottom content module */}
                                <SharedBottomModule
                                    className={styles.bottomModule}
                                    bottomModule={bottomModule}
                                />

                                {/* Questions & Answers */}
                                <SharedFAQ itemSearch={itemSearch} />

                                {/* Recently Viewed Items */}
                                <RecentlyViewedItemsLazy
                                    centerTitle
                                    wrapperClassName={styles.recentlyViewed}
                                    authModalLoader={authModalLoader}
                                    currency={currency}
                                />

                                {/* Related buy pages */}
                                <SharedRelatedPages
                                    viewer={viewer}
                                    itemSearch={itemSearch}
                                    LinksBlockComponent={SharedRespLinksBlock}
                                />
                            </>
                        )}
                    </SharedCreatorsRootHeadingsWrapper>
                </Fragment>
            )}
        </SharedFollowSearchProvider>
    );
};

SbRespCreatorsComponentsComponent.propTypes = {
    itemSearch: PropTypes.object.isRequired,
    user: PropTypes.object,
    isClient: PropTypes.bool,
    viewer: PropTypes.object,
    uriRef: PropTypes.string,
    relay: PropTypes.object.isRequired,
    currency: PropTypes.string.isRequired,
};

const mapStateToProps = state => ({ uriRef: state?.relayVariables?.variables?.uriRef });

export const SbRespCreatorsComponents = createFragmentContainer(
    connect(mapStateToProps)(SbRespCreatorsComponentsComponent),
    {
        viewer: graphql`
            fragment SbRespCreatorsComponents_viewer on Viewer {
                isWordpressArticlesEnabled: isEnabledByLocale(featureName: "WP_EDITORIAL")
                isRelatedCreatorsModuleEnabled: featureFlag(feature: "relatedCreatorsModule")
                isRelatedBuyPagesEnabled: isEnabledByLocale(featureName: "RELATED_BUY_PAGES")
                ...SbSharedCategoryTabs_viewer
                ...SharedRelatedPages_viewer
                ...SharedRespLinksBlock_viewer
                ...SbSharedTopTileController_viewer
                creatorRootPage(uriRef: $uriRef) {
                    ...SbSharedRelatedSellers_creatorRootPage
                    ...SbSharedCreatorTopContent_creatorRootPage
                    ...SbSharedCategoryTabs_creatorRootPage
                    ...SbSharedRelatedCreatorBuyPages_creatorRootPage
                }
            }
        `,
        user: graphql`
            fragment SbRespCreatorsComponents_user on User {
                ...SbRespFollowSearchBanner_user
                ...SharedFollowTopButton_user
                ...SbSharedTitle_user
                ...SharedFollowSearchProvider_user
                ...SbSharedRelatedSellers_user
                ...SbSharedPageBreadcrumbs_user
                ...SbSharedCreatorTopContent_user
            }
        `,
        itemSearch: graphql`
            fragment SbRespCreatorsComponents_itemSearch on ItemSearchQueryConnection {
                ...SbSharedPageBreadcrumbs_itemSearch
                ...SbSharedTitle_itemSearch
                ...SbSharedTopTileController_itemSearch
                ...SbSharedRelatedSearches_itemSearch
                ...SbRespDesignTiles_itemSearch
                ...SharedFollowSearchProvider_itemSearch
                ...SharedWordpressArticles_itemSearch
                ...SharedFollowTopButton_itemSearch
                ...SharedRelatedPages_itemSearch
                ...SharedRespLinksBlock_itemSearch
                ...SharedCreatorsRootHeadingsWrapper_itemSearch
                ...SbSharedCreatorTopContent_itemSearch
                ...SbSharedRelatedCreators_itemSearch
                ...SbSharedCategoryTabs_itemSearch
                ...SbSharedRelatedSellers_itemSearch
                bottomModule {
                    ...SharedBottomModule_bottomModule
                }
                designPageInfo(maxCreatorDesigns: 16) {
                    ...SbRespCreatorDesigns_designPageInfo
                    ...SbRespDesignTiles_designPageInfo
                }
                ...SharedFAQ_itemSearch
                ...SbRespFollowSearchBanner_itemSearch
            }
        `,
    }
);
